<template>
  <v-dialog
    persistent
    v-model="dialog"
    :fullscreen="$vuetify.breakpoint.mdAndDown"
    width="100%"
    max-width="700px"
  >
    <v-card style="padding: 20px !important">
      <v-btn
        @click="dialog = false"
        v-if="!user?.shouldChangePassword"
        class="position-absolute"
        icon
        top
        right
        absolute
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <!-- Hidden input -->
      <input
        accept=".jpg,.png,.jpeg"
        type="file"
        ref="hiddenInput"
        hidden
        @click="$event.target.value = ''"
        @change="processAvatar"
      />
      <h1 class="text-h5 font-weight-medium text-center">Account Settings</h1>
      <p class="text-center text-subtitle-2 font-weight-regular mt-3">
        Update your avatar & password here
      </p>

      <v-tabs v-model="tab" grow class="mt-10" active-class="font-weight-bold">
        <v-tab class="text-capitalize">Avatar</v-tab>
        <v-tab class="text-capitalize">Password</v-tab>

        <v-tab-item class="pa-4">
          <div v-if="!changePassword" class="d-flex justify-space-between">
            <div>
              <v-avatar
                v-if="!getUserAvatar"
                class="mx-auto d-block mt-7"
                size="150"
                color="grey lighten-3"
              >
                <v-icon size="70">mdi-account-circle-outline</v-icon>
              </v-avatar>
              <v-avatar v-else class="mx-auto d-block mt-7" size="150" color="">
                <v-img :src="getUserAvatar"></v-img>
              </v-avatar>
              <div class="text-center mt-5">
                <v-btn
                  @click="$refs.hiddenInput.click()"
                  class="text-capitalize text-caption"
                  color="primary"
                  depressed
                  >Upload avatar</v-btn
                >
                <v-btn
                  @click="removeAvatar"
                  class="text-capitalize text-caption ml-2"
                  depressed
                  >Remove avatar</v-btn
                >
              </div>
            </div>

            <div class="mt-10">
              <SignaturePicker />
            </div>
          </div>
          <v-sheet
            v-else
            width="100%"
            color="grey lighten-3"
            height="300"
            rounded="lg"
            class="mt-5"
          >
          </v-sheet>
        </v-tab-item>
        <v-tab-item class="pa-4 px-md-16">
          <v-alert
            v-if="$route.query?.change === 'true'"
            color="primary"
            border="left"
            text
          >
            <span class="text-caption font-weight-regular">
              <strong>Note:</strong> You will be logged out after changing your
              password
            </span>
          </v-alert>
          <v-alert color="primary" border="left" text>
            <div class="text-caption font-weight-regular">
              <strong>Password requirements</strong> <br />
              <ul>
                <li>8 characters or more</li>
                <li>At least one uppercase letter</li>
                <li>At least one lowercase letter</li>
                <li>At least one number</li>
                <li>At least one special character</li>
              </ul>
            </div>
          </v-alert>
          <v-form @submit.prevent="submit" class="mt-5">
            <ValidationObserver ref="passwordObserver">
              <ValidationProvider
                name="Old Password"
                rules="required|min:6"
                events="['blur']"
                v-slot="{ errors }"
              >
                <label class="text-subtitle-2 mb-1 d-inline-block" for="old"
                  >Old Password*</label
                >
                <v-text-field
                  :append-icon="
                    inputType === 'password' ? 'mdi-eye' : 'mdi-eye-off'
                  "
                  @click:append="toggleInputType"
                  :type="inputType"
                  v-model="oldPassword"
                  :error-messages="errors"
                  :rules="[]"
                  class="rounded"
                  dense
                  outlined
                  id="old"
                />
              </ValidationProvider>
              <ValidationProvider
                name="New Password"
                rules="required|min:8"
                events="['blur']"
                v-slot="{ errors }"
              >
                <label class="text-subtitle-2 mb-1 d-inline-block" for="new"
                  >New Password*</label
                >
                <v-text-field
                  :append-icon="
                    inputType === 'password' ? 'mdi-eye' : 'mdi-eye-off'
                  "
                  @click:append="toggleInputType"
                  :type="inputType"
                  v-model="password"
                  :error-messages="errors"
                  :rules="[strongPassword]"
                  dense
                  outlined
                  id="new"
                />
              </ValidationProvider>
              <ValidationProvider
                name="Confirm Password"
                rules="required|min:8"
                events="['blur']"
                v-slot="{ errors }"
              >
                <label class="text-subtitle-2 mb-1 d-inline-block" for="new"
                  >Confirm Password*</label
                >
                <v-text-field
                  :append-icon="
                    inputType === 'password' ? 'mdi-eye' : 'mdi-eye-off'
                  "
                  @click:append="toggleInputType"
                  :type="inputType"
                  v-model="confirmPassword"
                  :error-messages="errors"
                  :rules="[strongPassword, passwordMatch]"
                  dense
                  outlined
                />
              </ValidationProvider>
              <v-btn
                :loading="loading"
                type="submit"
                class="text-capitalize text-body-2 mt-2"
                color="primary"
                depressed
                >Update Password</v-btn
              >
            </ValidationObserver>
          </v-form>
        </v-tab-item>
      </v-tabs>
    </v-card>
  </v-dialog>
</template>

<script>
import PageMixin from '@/mixins/page.mixin';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import SharedMixin from '@/modules/shared/mixins/shared.mixin';
import SignaturePicker from './SignaturePicker.vue';

export default {
  mixins: [PageMixin, SharedMixin],
  components: { SignaturePicker },
  async created() {
    document.title = 'Account Settings';
    if (this.user) {
      const user = await this.fetchUser(this.user?.id);
      this.SET_USER(user);
    }
  },
  data: () => {
    return {
      dialog: false,
      changePassword: false,
      oldPassword: null,
      password: null,
      confirmPassword: null,
      loading: false,
      tab: 0,
      inputType: 'password',
      signatureImg: null
    };
  },
  computed: {
    ...mapGetters('authModule', ['user', 'getUserAvatar', 'getUserSignature'])
  },
  watch: {
    dialog(val) {
      if (!val) this.resetData();
    }
  },
  methods: {
    ...mapMutations('authModule', ['SET_USER']),
    toggleInputType() {
      this.inputType = this.inputType === 'password' ? 'text' : 'password';
    },
    resetData() {
      this.oldPassword = null;
      this.password = null;
      this.confirmPassword = null;
      this.$refs.passwordObserver?.reset();
      this.changePassword = false;
      this.loading = false;
      this.tab = 0;
      this.dialog = false;
    },
    /**
     * Method used to open the dialog
     * @param {Boolean} changePassword - If the user should change their password
     */
    open(changePassword = false) {
      this.dialog = true;
      this.changePassword = changePassword;
      if (changePassword) this.tab = 1;
    },
    ...mapActions('userModule', [
      'newPassword',
      'uploadAvatar',
      'deleteAvatar',
      'fetchUser'
    ]),
    strongPassword(value) {
      if (!value) return 'Password is required';
      const pattern =
        /^(?=.*\d)(?=.*[!@#$%^&*()-])(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
      if (!pattern.test(value))
        return 'Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character';
      return true;
    },
    passwordMatch(value) {
      if (!value) return 'Password is required';
      if (value !== this.password) return 'Passwords do not match';
      return true;
    },

    /**
     * Method used to remove the avatar
     */
    async removeAvatar(showSnack = true, fetchTheUser = true) {
      // check if user exist
      if (!this.user) return;
      // check if avatar exist
      if (!this.getUserAvatar) return;
      try {
        this.loading = true;
        await this.deleteAvatar({ id: this.user.id });
        if (fetchTheUser) {
          // Get user
          const user = await this.fetchUser(this.user.id);
          this.SET_USER(user);
        }
        if (showSnack) {
          this.showSnack({
            color: 'success',
            title: `Avatar removed`,
            message: 'The image was removed successfully'
          });
        }
      } catch (error) {
        this.showSnack({
          color: 'error',
          title: `Failed to delete avatar`,
          message: 'Please try again later'
        });
      } finally {
        this.loading = false;
      }
    },
    // function used to upload avatar
    async processAvatar(e) {
      try {
        // validate file
        if (!e) return;
        const file = e.target.files[0];
        if (!file) return;
        // check if file is an image
        if (!file.type.match('image/*'))
          return this.showSnack({
            color: 'error',
            title: `Invalid image`,
            message: 'Only images can be uploaded'
          });

        // create form data to be sent
        let fd = new FormData();
        fd.append('file', file);
        // Remove previous avatar
        await this.removeAvatar(false, false);
        // upload image
        await this.uploadAvatar({ data: fd, id: this.user.id });

        // Get user
        const user = await this.fetchUser(this.user.id);
        this.SET_USER(user);

        this.showSnack({
          color: 'success',
          title: `Avatar uploaded`,
          message: 'Your avatar has been updated'
        });
      } catch (error) {
        this.showSnack({
          color: 'error',
          title: `Something went wrong`,
          message: 'Please try uploading the image again'
        });
      } finally {
        this.loading = false;
      }
    },
    // Function used to submit new password
    async submit() {
      try {
        const isValid = await this.$refs.passwordObserver.validate();
        if (!isValid) return;
        // check if new password and old password are the same
        if (this.password === this.oldPassword)
          return this.showSnack({
            color: 'error',
            title: `Old & new passwords are the same`,
            message: 'Please use a different password'
          });
        if (this.password !== this.confirmPassword)
          return this.showSnack({
            color: 'error',
            title: `Passwords do not match`,
            message: 'Please make sure the passwords match'
          });

        //Set loading state
        this.loading = true;

        await this.newPassword({
          id: this.user.id,
          password: this.password,
          currentPassword: this.oldPassword
        });
        this.showSnack({
          color: 'success',
          title: `Updated`,
          message: `Password was successfully updated`
        });
        // logout the user
        await this.logoutSingleUser(this.user.id);

        // if the changePassword value is set to true then log the user out
        if (this.changePassword) {
          //For production purposes
          await this.$store.dispatch('authModule/signOut', this.user.id);
          // For testing purposes
          this.$store.commit('authModule/CLEAR_TOKENS');
          this.$store.commit('authModule/CLEAR_USER');
          this.$router.replace('/auth/login');
          this.showSnack({
            color: 'success',
            title: `Updated`,
            message: `Please log in again with your new password.`
          });

          setTimeout(() => {
            this.$router.go(0);
          }, 500);
        }

        //reset
        this.resetData();
        this.dialog = false;
      } catch (error) {
        this.showSnack({
          color: 'error',
          title: `Something went wrong`,
          message: error
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
